import React, { useMemo, useState, useCallback } from 'react'
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
  LabelList,
} from 'recharts'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import moment from 'moment/moment'

import EmptyScreen from 'packages/ui/EmptyScreen'
import { TimeRange } from 'models'
import { BarChartSkeleton } from 'components/global/CUK/Skeletons/BarChartSkeleton'

import useUtils from '../use-utils'
import TooltipGraph from '../components/TooltipGraph'
import useGraph from './use-graph'
import Legend from '../components/Legend'
import { Stack } from '@mui/system'
import { renderTotalLabel } from '../../../helpers'

interface NetworkConsumptionProps {
  modalTimeRange?: TimeRange
  hasLegend: boolean
}

const GraphConsumption: React.FC<NetworkConsumptionProps> = ({ modalTimeRange, hasLegend = false }) => {
  const { t, i18n } = useTranslation()
  const { loading, graph } = useGraph({ modalTimeRange })
  const { customTickFormatter, customDataUsageTickFormatter } = useUtils()
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const chartData = useMemo(() => {
    return graph.map(({ date, networks, is_estimated, average_per_device_type }) => {
      const privateValue = networks.filter(({name}) => name === 'private')
        .reduce((sum, network) => sum + network.upload + network.download, 0)
      const guestValue = networks.filter(({name}) => name === 'guest')
        .reduce((sum, network) => sum + network.upload + network.download, 0)
      const otherValue = networks.filter(({name}) => name === 'other')
        .reduce((sum, network) => sum + network.upload + network.download, 0)
      
      const total = privateValue + guestValue + otherValue
      
      return {
        timestamp: moment(date).unix() * 1000,
        private: privateValue,
        guest: guestValue,
        other: otherValue,
        total,
        is_estimated,
        average_per_device_type
      }
    })
  }, [graph])

  const hasData = useMemo(() => {
    if (!chartData.length) return false
    
    return chartData.some(entry => 
      (entry.private > 0) || 
      (entry.guest > 0) || 
      (entry.other > 0)
    )
  }, [chartData])

  const legendItems = useMemo(() => {
    return [
      { label: t('stats.legends.types.guest'), color: '#EC7A74' },
      { label: t('stats.legends.types.private'), color: '#4F8AA4' },
      { label: t('stats.legends.types.other'), color: '#A5D9CB' }
    ]
  }, [])

  const handleMouseMove = useCallback((data) => {
    if (data && data.activeTooltipIndex !== undefined) {
      setHoveredIndex(data.activeTooltipIndex)
    }
  }, [])

  if (loading) {
    return <BarChartSkeleton />
  }

  if (!hasData) {
    return (
      <Box sx={{ 
        width: '100%',
        height: '100%',
        border: '1px dashed #D7DAEA',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px'
      }}>
        <EmptyScreen 
          icon="square-poll-vertical" 
          variant="compact"
          iconSize="32px" 
          fontSize="14px"
          spacing={1.5}
        >
          {t('stats.traffic.graphs.no_data')}
        </EmptyScreen>
      </Box>
    )
  }

  const tooltipPosition = modalTimeRange ? { y: -120 } : { y: -200 }

  return (
    <Stack gap={1} height={'100%'}>
      <ResponsiveContainer>
        <BarChart
          width={500}
          height={300}
          data={chartData}
          margin={{
            top: 40,
            right: 30,
            left: 20,
            bottom: 5,
          }}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <XAxis
            dataKey="timestamp"
            tickFormatter={customTickFormatter}
            style={{
              fontSize: '0.75rem',
              fontWeight: 400,
            }}
          />
          <YAxis
            style={{
              fontSize: '0.75rem',
              fontWeight: 400,
            }}
            tickFormatter={customDataUsageTickFormatter}
          />
          <Tooltip
            cursor={false}
            wrapperStyle={{ outline: 'none', zIndex: 9999 }}
            content={<TooltipGraph dataType={'type'} />}
            animationDuration={0}
            position={tooltipPosition}
          />
          <Bar dataKey="other" stackId="a" fill="#A5D9CB" radius={[0, 0, 4, 4]} isAnimationActive={false}>
            {chartData.map((entry, index) => {
              const isHovered = hoveredIndex === index
              return (
                <Cell
                  key={`cell-other-${index}`}
                  fillOpacity={hoveredIndex === null ? 1 : (isHovered ? 1 : 0.6)}
                  stroke={entry.is_estimated ? '#A5D9CB' : 'none'}
                  strokeWidth={entry.is_estimated ? 1 : 0}
                  strokeDasharray={entry.is_estimated ? '3 3' : '0 0'}
                />
              )
            })}
          </Bar>
          <Bar dataKey="private" stackId="a" fill="#4F8AA4" radius={[0, 0, 0, 0]} isAnimationActive={false}>
            {chartData.map((entry, index) => {
              const isHovered = hoveredIndex === index
              return (
                <Cell
                  key={`cell-private-${index}`}
                  fillOpacity={hoveredIndex === null ? 1 : (isHovered ? 1 : 0.6)}
                  stroke={entry.is_estimated ? '#4F8AA4' : 'none'}
                  strokeWidth={entry.is_estimated ? 1 : 0}
                  strokeDasharray={entry.is_estimated ? '3 3' : '0 0'}
                />
              )
            })}
          </Bar>
          <Bar dataKey="guest" stackId="a" fill="#EC7A74" radius={[4, 4, 0, 0]} isAnimationActive={false}>
            <LabelList
              content={renderTotalLabel(chartData, i18n.language)}
            />
            {chartData.map((entry, index) => {
              const isHovered = hoveredIndex === index
              return (
                <Cell
                  key={`cell-guest-${index}`}
                  fillOpacity={hoveredIndex === null ? 1 : (isHovered ? 1 : 0.6)}
                  stroke={entry.is_estimated ? '#EC7A74' : 'none'}
                  strokeWidth={entry.is_estimated ? 1 : 0}
                  strokeDasharray={entry.is_estimated ? '3 3' : '0 0'}
                />
              )
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {
        hasLegend && <Legend items={legendItems} />
      }
    </Stack>
  )
}

export default GraphConsumption